import { useState, useEffect } from 'react'
import { Modal, Grid, Button, Spinner } from '@design-systems/canvas-ui'
import { Backdrop } from '@mui/material'
import EmailRow from './EmailRow'
import { getRfiTagValues } from '../../../../common/common'
import Icon, { ErrorFillIcon } from '@design-systems/react-icons'
import {
  getRfiEmailList,
  saveRfiEmailList,
} from '../../../../services/lcmService'
import CircularProgress from '@mui/material/CircularProgress'

const AUDIENCE_INTERNAL = 'INTERNAL'

/**
 * Modal popup for email audience selection whenever vetting action is hapening
 * @param {*} props
 * @returns
 */
const EmailModal = (props) => {
  //Get Emails
  const [isEmailFetchInProgress, setIsEmailFetchInProgress] = useState(true)
  const [isEmailFetchFailed, setIsEmailFetchFailed] = useState(false)

  const [toEmailList, setToEmailList] = useState([])
  const [ccEmailList, setCcEmailList] = useState([])
  const [isValidTO, setIsValidTO] = useState(false)
  const [isValidCC, setIsValidCC] = useState(false)
  const [isActionButtonEnabled, setIsActionButtonEnabled] = useState(true)

  //Save Emails
  const [isEmailSaveInProgress, setIsEmailSaveInProgress] = useState(false)
  const [isEmailSaveFailed, setIsEmailSaveFailed] = useState(false)

  useEffect(() => {
    if (props.isVisible) {
      getEmailList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isVisible])

  /**
   * Returns true if any email in TO or CC ends with '@target.com'.
   */
  const hasAtLeastOneTargetEmail = () => {
    const allEmails = [...toEmailList, ...ccEmailList]
    return allEmails.some((email) =>
      email.toLowerCase().endsWith('@target.com'),
    )
  }

  useEffect(() => {
    const isTargetEmailPresent = hasAtLeastOneTargetEmail()
    setIsActionButtonEnabled(isValidTO && isValidCC && isTargetEmailPresent)
  }, [isValidTO, isValidCC, toEmailList, ccEmailList])

  /**
   * Get email lists
   */
  const getEmailList = async () => {
    try {
      setIsEmailFetchInProgress(true)
      setIsEmailFetchFailed(false)
      let emailData = await getRfiEmailList(
        props.rfiType,
        props.supplierId,
        props.manufacturingTypeId,
        props.locationId,
      )
      if (props.audience?.toUpperCase() === AUDIENCE_INTERNAL) {
        //Flip the user lists asper the audience
        //External user -> To: Target users, Cc: Self company users
        //Internal user -> To: Supplier users, Cc: Target users
        if (emailData?.to) {
          setCcEmailList(emailData.to)
        }
        if (emailData?.cc) {
          setToEmailList(emailData.cc)
        }
      } else {
        if (emailData?.to) {
          setToEmailList(emailData.to)
        }
        if (emailData?.cc) {
          setCcEmailList(emailData.cc)
        }
      }

      setIsEmailFetchInProgress(false)
      setIsEmailFetchFailed(false)
    } catch (error) {
      setIsEmailFetchInProgress(false)
      setIsEmailFetchFailed(true)
    }
  }

  /**
   * Action click handler [Both Next (isSaveAndCloseCase = false) + Save & Close (isSaveAndCloseCase = true)]
   * isSaveAndCloseCase => true  : launch save and close message window
   *                       false : launch submit window
   * @param {*} isSaveAndCloseCase
   */
  const emailModalActionClickHandler = async (isSaveAndCloseCase) => {
    try {
      setIsEmailSaveInProgress(true)
      setIsEmailSaveFailed(false)
      //Flip the user lists asper the audience
      //External user -> To: Target users, Cc: Self company users
      //Internal user -> To: Supplier users, Cc: Target users
      let emailPayload = {}
      if (props.audience?.toUpperCase() === AUDIENCE_INTERNAL) {
        emailPayload = {
          to: ccEmailList,
          cc: toEmailList,
        }
      } else {
        emailPayload = {
          to: toEmailList,
          cc: ccEmailList,
        }
      }

      await saveRfiEmailList(
        emailPayload,
        props.rfiType,
        props.supplierId,
        props.manufacturingTypeId,
        props.locationId,
      )
      setIsEmailSaveInProgress(false)
      setIsEmailSaveFailed(false)
      props.closeEmailDialog(true, isSaveAndCloseCase)
    } catch (error) {
      setIsEmailSaveInProgress(false)
      setIsEmailSaveFailed(true)
    }
  }

  /**
   * Update if section is valid
   * @param {*} isSectionValid
   * @param {*} section
   */
  const updateIsAllValidEmailDetails = (isSectionValid, section) => {
    switch (section.toLowerCase()) {
      case 'to':
        setIsValidTO(isSectionValid)
        break
      case 'cc':
        setIsValidCC(isSectionValid)
        break
      default:
        break
    }
  }

  /**
   * Update Email list
   * @param {*} emailList
   * @param {*} section
   */
  const updateEmailList = (emailList, section) => {
    switch (section.toLowerCase()) {
      case 'to':
        setToEmailList(emailList)
        break
      case 'cc':
        setCcEmailList(emailList)
        break
      default:
        break
    }
  }

  return (
    <Modal
      id="modal-email-confirmation"
      headingText={
        'Preview of recipients who will be notified for the RFI workflow'
      }
      size="md"
      isOpen={props.isVisible}
      onClose={() => props.closeEmailDialog(false)}
      className="lcm-email-modal"
    >
      <p className="lcm-email-modal__instructions hc8-ph-6x hc8-pt-4x hc8-pb-2x hc8-mb-none hc8-fs-100 hc8-lh-body">
        {getRfiTagValues(
          props.audience?.toUpperCase() === AUDIENCE_INTERNAL
            ? 'rfiEmailCaptureInstructionInternal'
            : 'rfiEmailCaptureInstructionExternal',
        )}
      </p>
      <Grid.Container
        id="modal-email-content"
        className="lcm-email-modal__content hc8-pb-none hc8-mb-none"
      >
        {!isEmailFetchInProgress && !isEmailFetchFailed && (
          <>
            <Grid.Item size={{ xs: 12 }} className="lcm-email-modal__rows">
              <EmailRow
                key={'email-list-to'}
                emailRecipientGroup={'TO'}
                emailList={toEmailList}
                isFocus={true}
                updateIsAllValidEmailDetails={updateIsAllValidEmailDetails}
                updateEmailList={updateEmailList}
              />
              <EmailRow
                key={'email-list-cc'}
                emailRecipientGroup={'CC'}
                emailList={ccEmailList}
                updateIsAllValidEmailDetails={updateIsAllValidEmailDetails}
                updateEmailList={updateEmailList}
              />
            </Grid.Item>

            <Grid.Item
              size={{ xs: 12 }}
              id="container-email-actions"
              className="lcm-email-modal__actions hc8-ph-6x hc8-pt-3x hc8-pb-6x"
            >
              {!isActionButtonEnabled && (
                <label
                  id="email-instruction-disclaimer"
                  className="lcm-email-modal__validation hc8-mr-4x hc8-clr-error hc8-fs-050"
                >
                  {getRfiTagValues(
                    isValidTO && isValidCC && !hasAtLeastOneTargetEmail()
                      ? 'emailMustIncludeTargetDomain'
                      : 'emailPopupActionInstruction',
                  )}
                </label>
              )}

              {!isEmailSaveInProgress && isEmailSaveFailed && (
                <Icon
                  svg={ErrorFillIcon}
                  className="hc8-clr-error"
                  size="lg"
                  aria-hidden
                />
              )}
              <Button
                id="button-email-save"
                className="lcm-email-modal__button hc8-ml-2x"
                variant="secondary"
                size="md"
                onClick={() => {
                  emailModalActionClickHandler(true)
                }}
                isDisabled={!isActionButtonEnabled}
              >
                Save & Close
              </Button>
              {props.onBack && (
                <Button
                  id="button-email-back"
                  className="lcm-email-modal__button hc8-ml-2x"
                  variant="secondary"
                  size="md"
                  onClick={() => props.onBack()}
                >
                  Back
                </Button>
              )}
              <Button
                id="button-email-next"
                className="lcm-email-modal__button hc8-ml-2x"
                variant="primary"
                size="md"
                onClick={() => {
                  emailModalActionClickHandler(false)
                }}
                isDisabled={!isActionButtonEnabled}
              >
                Next
              </Button>
            </Grid.Item>
          </>
        )}
        {isEmailFetchInProgress && !isEmailFetchFailed && (
          <Grid.Container
            className="lcm-email-modal__state"
            data-testid={`spinner-email-loading`}
          >
            <Spinner size="lg" />
          </Grid.Container>
        )}
      </Grid.Container>
      {!isEmailFetchInProgress && isEmailFetchFailed && (
        <Grid.Container
          id="modal-email-error"
          className="lcm-email-modal__state lcm-email-modal__state--retry"
          onClick={() => getEmailList()}
        >
          <Icon
            svg={ErrorFillIcon}
            className="hc8-clr-error"
            size="xl"
            aria-hidden
          />
        </Grid.Container>
      )}
      <Backdrop
        className="backdrop-style"
        open={isEmailSaveInProgress && !isEmailSaveFailed}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Modal>
  )
}

export default EmailModal
